import { HeaderDesignControllerContextData } from 'src/hooks/useHeaderDesignController';
import { palette } from 'src/styles/colors';
import { layout } from 'src/styles/layout';
import styled, { css } from 'styled-components';

interface HeaderContainerProps {
  backgroundTheme: HeaderDesignControllerContextData['backgroundTheme'];
  isPined?: boolean;
  scaled?: boolean;
}

export const HEADER_INITIAL_HEIGHT_IN_PIXELS = 88;
export const HEADER_HEIGHT_IN_PIXELS = 66;
export const HEADER_HEIGHT_IN_PIXELS_MOBILE = 64;
export const HEADER_INITIAL_HEIGHT_SCREEN_SIZE_BASED_IN_VH = 12;
export const HEADER_SCREEN_SIZE_BASED_IN_VH = 9;

export const getCurrentHeaderHeight = (isPined = false): number => {
  // 700: sm window size
  if (window.innerWidth <= 700) {
    return HEADER_HEIGHT_IN_PIXELS_MOBILE;
  }

  return isPined ? HEADER_INITIAL_HEIGHT_IN_PIXELS : HEADER_HEIGHT_IN_PIXELS;
};

export const HeaderFixedContainer = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  z-index: 100;
`;

export const HeaderContainer = styled.div<HeaderContainerProps>`
  position: relative;
  width: 100vw;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  background-color: var(--header-background-color-theme);
  transition:
    background-color 400ms ease-in-out,
    backdrop-filter 400ms ease-in-out;

  .styled-menu-link {
    transition: color 300ms ease-in-out;
  }

  ${({ backgroundTheme }) => {
    if (backgroundTheme === 'dark') {
      return css`
        --header-background-color-theme: ${palette.greyText};
      `;
    }

    if (backgroundTheme === 'light') {
      return css`
        --header-background-color-theme: ${palette.white};
      `;
    }

    if (backgroundTheme === 'transparent') {
      return css`
        --header-background-color-theme: transparent;
      `;
    }

    // blurred
    return css`
      --header-background-color-theme: rgba(20, 25, 33, 0.01);
      backdrop-filter: blur(12px);
    `;
  }}

  ${({ isPined, scaled }) =>
    !scaled
      ? css`
          height: ${isPined ? HEADER_INITIAL_HEIGHT_IN_PIXELS : HEADER_HEIGHT_IN_PIXELS}px;
          transition:
            background-color 400ms ease-in-out,
            backdrop-filter 400ms ease-in-out,
            height 500ms ease-in-out;
        `
      : css`
          height: ${isPined ? HEADER_INITIAL_HEIGHT_IN_PIXELS : HEADER_HEIGHT_IN_PIXELS}px;
          transition:
            background-color 400ms ease-in-out,
            backdrop-filter 400ms ease-in-out,
            height 500ms ease-in-out;

          @media screen and (max-width: ${layout.sm}) {
            height: ${HEADER_HEIGHT_IN_PIXELS_MOBILE}px;
          }

          /* Adjust size based on screen width, only applies to animated experience */
          @media screen and (min-width: ${layout.xxlg}) {
            height: ${isPined ? HEADER_INITIAL_HEIGHT_SCREEN_SIZE_BASED_IN_VH : HEADER_SCREEN_SIZE_BASED_IN_VH}vh;
            .animated-mighty-logo-container {
              /* Equivalent to 100px on 1440px */
              width: 7vw;
            }

            .mighty-pro-logo {
              /* Equivalent to 152px on 1440px */
              width: 10.5vw;
            }

            .styled-menu-link {
              /* Equivalent to 1.5rem on 2560px */
              font-size: 0.9vw;
            }

            .link-branded {
              font-size: 0.9vw;
              padding: 0.6vw 1.2vw; // Equivalent to 8px 16px on 1440px
              border-radius: 1.25vw; // Equivalent to 18px on 1440px

              .icon-right svg {
                /* Equivalent to 15px and 10px on 1440px */
                width: 1.1vw;
                height: 0.7vw;
              }
            }
          }
        `}
`;

interface ActionsContainerProps {
  textColorTheme: HeaderDesignControllerContextData['textColorTheme'];
  showHamburgerButtonOnDesktop?: boolean;
}

export const ActionsContainer = styled.div<ActionsContainerProps>`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  justify-content: space-between;
  margin-right: 5rem;
  padding-left: 6rem;
  transform: translateX(0);
  --header-text-color-theme: ${(props) => (props.textColorTheme === 'light' ? palette.white : palette.greyText)};

  #sign-in-link {
    margin-right: 24px;
    margin-left: 9px;
  }

  ${(props) =>
    props.showHamburgerButtonOnDesktop
      ? ''
      : css`
          .hamburger-button {
            display: none;
          }
        `}

  ${(props) =>
    props.textColorTheme === 'light'
      ? ''
      : css`
          path {
            fill: ${palette.greyText};
          }
        `}

  /* Adapt margin based on the screen height, only on large screens */
  @media screen and (min-width: ${layout.xxlg}) {
    &.force-scale {
      margin-right: 10vw;
      padding-left: calc(10vw + 2rem);
    }
  }

  @media screen and (max-width: ${layout.subxlg}) {
    margin-right: 3rem;
    padding-left: 4rem;
  }

  @media screen and (max-width: ${layout.lg}) {
    /* Hide sing in link, and all menu links */
    #sign-in-link,
    .styled-menu-link {
      display: none;
    }

    .hamburger-button {
      display: block;
      margin-left: 1rem;
    }
  }

  @media screen and (max-width: ${layout.md}) {
    .link-branded .icon-right {
      display: none;
    }
  }

  @media screen and (max-width: ${layout.sm}) {
    margin-right: 1.5rem;

    .link-branded {
      padding: 8px 11px;
    }

    .hamburger-button {
      margin-left: 0.875rem;
    }
  }
`;

export const ActionsLinkContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex: 1 1 auto;
`;
